import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import QrScanner from 'qr-scanner';

interface ParticipantQrScannerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (qrPayload: string) => void;
}

function ParticipantQrScannerDialog({ isOpen, onClose, onDetected }: ParticipantQrScannerDialogProps) {
  const scannerRef = useRef<QrScanner | null>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [scannerStatus, setScannerStatus] = useState('Starting camera...');

  const setVideoRef = useCallback((element: HTMLVideoElement | null) => {
    setVideoElement(element);
  }, []);

  const stopScanner = useCallback(() => {
    try {
      scannerRef.current?.stop();
      scannerRef.current?.destroy();
    } catch {
      // Scanner may already be stopped.
    }

    scannerRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen || !videoElement) {
      return undefined;
    }

    let isMounted = true;

    const startScanner = async () => {
      try {
        setScannerError(null);
        setScannerStatus('Checking camera access...');

        if (!window.isSecureContext) {
          setScannerError('Camera access requires HTTPS or localhost.');
          setScannerStatus('Camera unavailable');
          return;
        }

        const hasCamera = await QrScanner.hasCamera();

        if (!hasCamera) {
          setScannerError('No camera was found on this device.');
          setScannerStatus('Camera unavailable');
          return;
        }

        stopScanner();

        const scanner = new QrScanner(
          videoElement,
          (result) => {
            const qrPayload = typeof result === 'string' ? result : result.data;

            if (!qrPayload) {
              return;
            }

            onDetected(qrPayload);
            stopScanner();
            onClose();
          },
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 8,
            calculateScanRegion: (video) => {
              const smallestDimension = Math.min(video.videoWidth, video.videoHeight);

              const scanRegionSize = Math.round(smallestDimension * 0.75);

              return {
                x: Math.round((video.videoWidth - scanRegionSize) / 2),
                y: Math.round((video.videoHeight - scanRegionSize) / 2),
                width: scanRegionSize,
                height: scanRegionSize,
                downScaledWidth: 600,
                downScaledHeight: 600,
              };
            },
          },
        );

        scannerRef.current = scanner;

        setScannerStatus('Starting camera stream...');

        await scanner.start();

        if (!isMounted) {
          stopScanner();
          return;
        }

        setScannerStatus('Camera ready. Place the QR code inside the scan box.');
      } catch (error) {
        setScannerError(error instanceof Error ? error.message : 'The camera scanner could not be started.');
        setScannerStatus('Camera unavailable');
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      stopScanner();
    };
  }, [isOpen, videoElement, onClose, onDetected, stopScanner]);

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      keepMounted={false}
      TransitionProps={{
        onEntered: () => {
          setScannerStatus('Preparing camera...');
        },
        onExited: () => {
          stopScanner();
          setVideoElement(null);
          setScannerError(null);
          setScannerStatus('Starting camera...');
        },
      }}
    >
      <DialogTitle>Scan Participant QR Code</DialogTitle>

      <DialogContent>
        <Typography variant="body1" component="p" color="text.secondary" sx={{ mb: 2 }}>
          Point the camera at the participant QR code shown in the secure Participant Portal. Keep the QR code inside
          the scan box and hold the device steady.
        </Typography>

        {scannerError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {scannerError}
          </Alert>
        )}

        {!scannerError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {scannerStatus}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            height: { xs: 360, md: 480 },
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'black',
            border: 1,
            borderColor: 'divider',
            position: 'relative',

            '& video': {
              width: '100% !important',
              height: '100% !important',
              objectFit: 'cover',
              display: 'block',
            },

            /*
             * Do not hide canvas elements.
             * qr-scanner uses canvas overlays for the scan region and QR outline.
             */
            '& canvas': {
              display: 'block',
            },
          }}
        >
          <video
            ref={setVideoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: 'black',
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ParticipantQrScannerDialog;
